import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import GridOnIcon from '@mui/icons-material/GridOn';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

/**
 * ファイル名から拡張子を取得（小文字、ドットなし）
 */
function extensionFromFilename(filename: string | undefined): string {
  if (!filename) return '';
  const base = filename.split(/[/\\]/).pop() || filename;
  const i = base.lastIndexOf('.');
  if (i <= 0 || i === base.length - 1) return '';
  return base.slice(i + 1).toLowerCase();
}

/**
 * 掲示板添付の MIME（と任意でファイル名）から MUI アイコンコンポーネントを返す
 */
export function resolveBulletinAttachmentIcon(
  contentType: string,
  filename?: string
): ComponentType<SvgIconProps> {
  const ct = (contentType || '').trim().toLowerCase();
  const ext = extensionFromFilename(filename);

  const byExt = (): ComponentType<SvgIconProps> | null => {
    if (ext === 'pdf') return PictureAsPdfIcon;
    if (ext === 'doc' || ext === 'docx') return DescriptionIcon;
    if (ext === 'xls' || ext === 'xlsx') return GridOnIcon;
    if (ext === 'txt' || ext === 'text') return ArticleIcon;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif'].includes(ext)) return ImageIcon;
    return null;
  };

  if (ct.startsWith('image/')) return ImageIcon;
  if (ct === 'application/pdf' || ct.includes('pdf')) return PictureAsPdfIcon;
  if (ct === 'application/msword' || ct.includes('wordprocessingml') || ct.includes('msword')) {
    return DescriptionIcon;
  }
  if (
    ct === 'application/vnd.ms-excel' ||
    ct.includes('spreadsheetml') ||
    ct.includes('excel')
  ) {
    return GridOnIcon;
  }
  if (ct === 'text/plain' || ct.startsWith('text/')) return ArticleIcon;

  if (!ct || ct === 'application/octet-stream') {
    const fromExt = byExt();
    if (fromExt) return fromExt;
  }

  return InsertDriveFileIcon;
}

type TypeIconProps = SvgIconProps & {
  contentType: string;
  originalFilename?: string;
};

/** 添付の種類を表すアイコン（props で size / color を渡せる） */
export function BulletinAttachmentTypeIcon({
  contentType,
  originalFilename,
  ...rest
}: TypeIconProps) {
  const Icon = resolveBulletinAttachmentIcon(contentType, originalFilename);
  return <Icon {...rest} />;
}
